import type { Booking } from "@/types/Booking";
import type { Member } from "@/types/Member";
import { resolveBufferFlags } from "@/utils/bufferFlags";

type BuffersProps = {
  booking: Booking;
  member?: Member | null;
  top: number;
  height: number;
  half: number;
  /**
   * Where these buffers are drawn. Amenity columns sit on a much darker background
   * (`bg-gray-400/20`) than room columns, against which the default light hatch is invisible —
   * see `onDarkColumn` below.
   */
  variant?: "room" | "amenity";
};

const Buffers = ({ booking, member, top, height, half, variant = "room" }: BuffersProps) => {
  // A member's OWN bookings render solid black (see Bubble.tsx). Their buffers should match with a
  // black-and-white diagonal hatch rather than the neutral grey hatch (or the room swatch in disco
  // mode). Amenities carry their parent's memberId, so an own booking's amenity buffer matches too.
  const isMyBooking = !!member && booking.memberId === member.id;

  // The other-member hatch is deliberately faint on room columns, whose tints are light
  // (bg-gray-100/10 / bg-purple-100/20). Amenity columns use bg-gray-400/20, which composites to
  // ~rgb(235,237,239) — against that, the #eaeaea stripe is a 1.02:1 contrast ratio and simply
  // cannot be seen. That is why a PM booking an amenity ON BEHALF of a member appeared to get no
  // buffer while booking for THEMSELVES appeared to work: the buffer was always there, but the
  // on-behalf tile takes the other-member (faint) branch because amenity tiles carry the
  // BOOKING's memberId, not the operator's. Darken the stripe on amenity columns so both cases
  // read identically.
  const onDarkColumn = variant === "amenity";
  const otherMemberHatch = onDarkColumn
    ? "dark:bg-[repeating-linear-gradient(45deg,#a0a0a0_0_2px,transparent_2px_7px)] bg-[repeating-linear-gradient(45deg,#94a3b8_0_2px,transparent_2px_7px)]"
    : "dark:bg-[repeating-linear-gradient(45deg,#a0a0a0_0_1px,transparent_2px_6px)] bg-[repeating-linear-gradient(45deg,#eaeaea_0_1px,transparent_2px_6px)]";

  const hatchClass = isMyBooking
    ? "bg-[repeating-linear-gradient(45deg,#000_0_2px,#fff_2px_8px)] dark:bg-[repeating-linear-gradient(45deg,#000_0_2px,#fff_2px_8px)]"
    : `${member?.settings?.discoModeEnabled ? booking.room?.swatch : ""} ${otherMemberHatch}`;

  // Buffers are directional: draw the top (pre) buffer only when the booking has a pre-buffer, and
  // the bottom (post) buffer only when it has a post-buffer. Resolving from the directional flags
  // (not the combined `hasBuffers`, which is stored as pre && post) is what lets a single-sided
  // buffer render — previously pre-only/post-only hid BOTH.
  const { pre, post } = resolveBufferFlags(booking);

  return (
    <>
      {pre && (
        <div
          aria-label="15 minute buffer before booking"
          data-testid="booking-buffer-top"
          className={`absolute w-full z-20 rounded-t-md pointer-events-auto ${hatchClass}`}
          style={{
            height: `${half}px`,
            top: `${top - half}px`,
          }}
        >
          &nbsp;
        </div>
      )}
      {post && (
        <div
          aria-label="15 minute buffer after booking"
          data-testid="booking-buffer-bottom"
          className={`absolute w-full z-20 rounded-b-md pointer-events-auto ${hatchClass}`}
          style={{
            height: `${half}px`,
            top: `${top + height}px`,
          }}
        >
          &nbsp;
        </div>
      )}
    </>
  );
};

export default Buffers;
